import { cloneDeep, isDate } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { combineRefs } from '../../../common/functions/combine-refs';
import useTestTag from '../../../common/hooks/use-test-tag';
import { ContinuousScrollerComparisonType } from '../../continuous-scroller/continuous-scroller-types';
import type { DateRange } from '../../date-filter-popover/date-range-filter.component';
import DateRangeFilter from '../../date-filter-popover/date-range-filter.component';
import {
  formatDateRange,
  getCustomDateRangeItem,
  getDateRanges,
} from '../../date-filter-popover/date-range-filter-functions';
import type { DropDownResult } from '../../fields/drop-down/drop-down-types';
import { hasColumnChanged } from '../data-grid-functions';
import type { DataGridColumnSettings } from '../data-grid-settings.interface';
import type { DataGridFilterProps } from './data-grid-filter.interface';

export default function DataGridDateFilter({
  column,
  filterSignal,
  forceResetColumnMap,
}: DataGridFilterProps<DateRange>) {
  const [currentValue, setCurrentValue] = useState<DateRange | undefined>(
    transformDataGridColumnSearchValueToDateRange(column.searchValue)
  );

  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<string | undefined>(
    column.customDataProperties?.dateRange as string | undefined
  );

  const previousColumn = useRef<DataGridColumnSettings | undefined>(undefined);
  const columnRef = useRef<HTMLDivElement | null>(null);

  const tagCreator = useTestTag(column.title || '');
  const combinedRef = combineRefs(columnRef, tagCreator('date-filter'));

  useEffect(() => {
    if (hasColumnChanged(forceResetColumnMap, column, previousColumn.current)) {
      const value = transformDataGridColumnSearchValueToDateRange(column.searchValue);
      setCurrentValue(value);

      if (column.customDataProperties?.dateRange) {
        const dateRange = getDateRanges().find((range) => range.value === column.customDataProperties!.dateRange);
        setSelectedRange(dateRange?.text);
      } else if (value?.from || value?.to) {
        setSelectedRange(formatDateRange(value?.from, value?.to));
      } else {
        setSelectedRange(undefined);
      }
    }

    previousColumn.current = column;
  }, [column, forceResetColumnMap]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Mount-only effect to initialize filter from initial column values
  useEffect(() => {
    if (currentValue && (currentValue.from || currentValue.to)) {
      const filterIndex = filterSignal.value.findIndex((filter) => filter.scrollerId === column.id);

      // Only add if filter doesn't already exist for this column
      if (filterIndex === -1) {
        const clonedFilters = cloneDeep(filterSignal.value);

        const continuousScrollerFilter = transformDateRangeToContinuousScrollerSearchValueFilter(
          column.id,
          currentValue,
          column.field,
          column.customDataProperties
        );

        clonedFilters.push(continuousScrollerFilter!);
        filterSignal.value = clonedFilters;
      }

      if (column.customDataProperties?.dateRange) {
        const dateRange = getDateRanges().find((range) => range.value === column.customDataProperties!.dateRange);
        setSelectedRange(dateRange?.text);
      } else {
        setSelectedRange(formatDateRange(currentValue?.from, currentValue?.to));
      }
    }
  }, []);

  const onDateRangeChange = (dateRange: DateRange, quickDateRange?: string) => {
    if (dateRange && (dateRange.from || dateRange.to)) {
      const filterIndex = filterSignal.value.findIndex((filter) => filter.scrollerId === column.id);
      const clonedFilters = cloneDeep(filterSignal.value);

      const customDataProperties = {
        ...column.customDataProperties,
        dateRange: quickDateRange,
      };

      const continuousScrollerFilter = transformDateRangeToContinuousScrollerSearchValueFilter(
        column.id,
        dateRange,
        column.field,
        customDataProperties
      );

      if (filterIndex > -1) {
        clonedFilters.splice(filterIndex, 1, continuousScrollerFilter!);
        filterSignal.value = clonedFilters;
      } else {
        clonedFilters.push(continuousScrollerFilter!);
        filterSignal.value = clonedFilters;
      }

      setCurrentValue(dateRange);
    } else {
      if (filterSignal.value.findIndex((filter) => filter.scrollerId === column.id) > -1) {
        filterSignal.value = filterSignal.value.filter((filter) => filter.scrollerId !== column.id);
      }
      setCurrentValue(undefined);
    }
  };

  const handleClearFilter = () => {
    setFilterPopoverOpen(false);
    setSelectedRange(undefined);
    onDateRangeChange({});
  };

  const handleHidePanel = () => {
    setFilterPopoverOpen(false);
  };

  const handleApplyFilter = (dateRange: DateRange, quickDateRange?: DropDownResult) => {
    setFilterPopoverOpen(false);

    if (getCustomDateRangeItem().value === quickDateRange?.value) {
      setSelectedRange(formatDateRange(dateRange.from, dateRange.to));
      onDateRangeChange(dateRange);
    } else {
      setSelectedRange(quickDateRange?.text);
      onDateRangeChange(dateRange, quickDateRange?.value ? String(quickDateRange.value) : undefined);
    }
  };

  return (
    <div className="flex h-full" ref={combinedRef}>
      <DateRangeFilter
        value={currentValue}
        selectedQuickDateRange={selectedRange}
        open={filterPopoverOpen}
        onOpenChange={setFilterPopoverOpen}
        onApplyFilter={handleApplyFilter}
        onClearFilter={handleClearFilter}
        onHidePanel={handleHidePanel}
      />
    </div>
  );
}

// Transforms a column configuration search value into a DateRange
export const transformDataGridColumnSearchValueToDateRange = (
  searchValue?: string | boolean | DropDownResult | Array<DropDownResult> | Date[] | Date,
  comparison?: string
) => {
  if (searchValue === undefined) {
    return undefined;
  } else if (Array.isArray(searchValue) && searchValue.length >= 1) {
    return {
      from: isDate(searchValue[0]) ? searchValue[0] : undefined,
      to: isDate(searchValue[1]) ? searchValue[1] : undefined,
    };
  } else if (comparison === undefined || !isDate(searchValue)) {
    return undefined;
  } else {
    // we have only one date, so it's either a from or to date based on comparison type
    switch (comparison) {
      case '>=':
        return { from: searchValue as Date, to: undefined };
      case '<=':
        return { from: undefined, to: searchValue as Date };
      default:
        return undefined;
    }
  }
};

// Transforms the DataRange value into a continuous scroller filter
export const transformDateRangeToContinuousScrollerSearchValueFilter = (
  scrollerId: string | number,
  dateRange: DateRange,
  field: string,
  customDataProperties?: Record<string, unknown>
) => {
  const { from, to } = dateRange;

  if (from && to) {
    return {
      scrollerId,
      field,
      value: [from, to],
      comparison: ContinuousScrollerComparisonType.Between,
      customDataProperties,
    };
  } else if (from && !to) {
    return {
      scrollerId,
      field,
      value: from,
      comparison: ContinuousScrollerComparisonType.GreaterThanEqualTo,
      customDataProperties,
    };
  } else if (!from && to) {
    return {
      scrollerId,
      field,
      value: to,
      comparison: ContinuousScrollerComparisonType.LessThanEqualTo,
      customDataProperties,
    };
  }
};
